import React from 'react';
import { useTranslation } from 'react-i18next';
import css from './lessonSteps.module.css';

/**
 * LessonSteps — Unified sidebar block for lesson step navigation.
 * Based on Design 27. Must be identical for all lessons (regular + trial/demo).
 *
 * Props:
 *   currentStep  — number (1-4), which step is active
 *   onStepClick  — (stepNumber) => void, called when user clicks a step
 */
const SECTION_BY_STEP = {
  1: "video",
  2: "vocabulary",
  3: "quiz",
  4: "ai_dialog",
};

const LessonSteps = ({ currentStep = 1, onStepClick, isTrialLesson, sectionProgress, completedCount }) => {
  const { t } = useTranslation();
  const totalSteps = 4;
  const doneCount = typeof completedCount === "number"
    ? completedCount
    : Object.values(sectionProgress || {}).filter((section) => section?.status === "completed").length;

  const steps = [
    {
      num: 1,
      label: '01',
      title: 'Video',
      sub: t('lessonSteps.videoSub', 'Current Step'),
      activeSub: t('lessonSteps.currentStep', 'Current Step'),
      completedSub: t('lessonSteps.completed', 'Completed'),
      upcomingSub: t('lessonSteps.videoDesc', 'Watch Video'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      ),
    },
    {
      num: 2,
      label: '02',
      title: 'Words',
      activeSub: t('lessonSteps.currentStep', 'Current Step'),
      completedSub: t('lessonSteps.completed', 'Completed'),
      upcomingSub: t('lessonSteps.wordsSub', 'Learn Words'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
        </svg>
      ),
    },
    {
      num: 3,
      label: '03',
      title: 'Quiz',
      activeSub: t('lessonSteps.currentStep', 'Current Step'),
      completedSub: t('lessonSteps.completed', 'Completed'),
      upcomingSub: t('lessonSteps.quizSub', 'Check Understanding'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
      ),
    },
    {
      num: 4,
      label: '04',
      title: 'Speaking',
      activeSub: t('lessonSteps.currentStep', 'Current Step'),
      completedSub: t('lessonSteps.completed', 'Completed'),
      upcomingSub: t('lessonSteps.speakingSub', 'Practice Dialogues'),
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        </svg>
      ),
    },
  ];

  const getStatus = (stepNum) => {
    const sectionKey = SECTION_BY_STEP[stepNum];
    const sectionDone = sectionProgress?.[sectionKey]?.status === "completed";
    if (stepNum === currentStep) return "active";
    if (sectionDone) return "completed";
    return "upcoming";
  };

  const getSub = (step) => {
    const status = getStatus(step.num);
    if (status === 'completed') return step.completedSub;
    if (status === 'active') return step.activeSub;
    return step.upcomingSub;
  };

  return (
    <div className={css.stepsPanel}>
      <div className={css.stepsCard}>
        <div className={css.stepsHeader}>
          <div className={css.stepsTitle}>{t("Lesson steps")}</div>
          <div className={css.stepsCounter}>{doneCount} / {totalSteps}</div>
        </div>

        <div className={css.stepsList}>
          {/* Vertical connector line */}
          <div className={css.connector}>
            <div className={css.connectorFill} style={{ height: `${(doneCount / totalSteps) * 100}%` }} />
          </div>

          {steps.map((step) => {
            const status = getStatus(step.num);
            return (
              <div
                key={step.num}
                className={`${css.stepRow} ${css[status]}`}
                onClick={() => onStepClick && onStepClick(step.num)}
              >
                <div className={css.stepDot}>
                  {status === 'completed' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                  ) : (
                    step.icon
                  )}
                </div>
                <div className={css.stepBody}>
                  <div className={css.stepTitle}>{step.title}</div>
                  <div className={css.stepSub}>{t(getSub(step))}</div>
                </div>
                <div className={css.stepNum}>{step.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={css.microcopy}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z"/>
        </svg>
        { isTrialLesson ?
        <span>
          {t('In the full course, every lesson follows the same structure: Video → Words → Quiz → AI Speaking.')}
        </span> : <span>
          {t('Recommended order: Video → Words → Quiz → Speaking. Complete the steps in order.')}
        </span>}
      </div>
    </div>
  );
};

export default LessonSteps;
